import React, { useState, useEffect } from "react";
import { MdPersonAdd, MdLock, MdList, MdClear, MdExpandMore, MdExpandLess } from "react-icons/md";
import axiosInstance from "../../../../axiosConfig";

const DialogBox = ({ type, message, onConfirm, onCancel, isOpen, setIsOpen }) => {
  if (!isOpen) return null;

  const styles = {
    info: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-500" },
    error: { bg: "bg-red-100", text: "text-red-800", border: "border-red-500" },
    success: { bg: "bg-green-100", text: "text-green-800", border: "border-green-500" },
    prompt: { bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-500" },
  };

  const { bg, text, border } = styles[type] || styles.info;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`p-6 ${bg} ${text} rounded-lg shadow-xl border ${border} max-w-md w-full`}>
        <p className="text-lg font-medium mb-4">{message}</p>
        <div className="flex justify-end space-x-4">
          {type === "prompt" && (
            <>
              <button
                onClick={() => {
                  onCancel && onCancel();
                  setIsOpen(false);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onConfirm && onConfirm();
                  setIsOpen(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Confirm
              </button>
            </>
          )}
          {type !== "prompt" && (
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const GroupMaintenance = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // { group_type, name }
  const [formData, setFormData] = useState({
    group_type: "role",
    name: "",
    description: "",
  });
  const [availableRights, setAvailableRights] = useState({});
  const [assignedRights, setAssignedRights] = useState([]);
  const [expandedFunctionalities, setExpandedFunctionalities] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, type: "", message: "", onConfirm: null, onCancel: null });

  // Fetch groups from backend
  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/group-lst");
        if (response.data.status === "success") {
          setGroups(response.data.message);
        } else {
          setDialog({
            isOpen: true,
            type: response.data.status,
            message: response.data.message || "Failed to fetch groups.",
          });
        }
      } catch (err) {
        if (err.response?.status !== 403) {
          setDialog({
            isOpen: true,
            type: "error",
            message: err.response?.data?.message || "Failed to fetch groups. Please try again.",
          });
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  // Fetch group permissions when Assign Rights tab is clicked
  const fetchGroupPermissions = async (groupType, name) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/get-grp-perms?group_type=${groupType}&value=${name}`);
      if (response.data.status === "success") {
        const { available_rights, group_rights } = response.data.message;
        setAvailableRights(available_rights || {});
        setAssignedRights(group_rights || []);
        setExpandedFunctionalities(
          Object.keys(available_rights || {}).reduce((acc, key) => ({ ...acc, [key]: false }), {})
        );
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || "Failed to fetch group permissions.",
        });
      }
    } catch (err) {
      if (err.response?.status !== 403) {
        setDialog({
          isOpen: true,
          type: "error",
          message: err.response?.data?.message || "Failed to fetch group permissions. Please try again.",
        });
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleGroupSelect = (group) => {
    const newSelection = selectedGroup && selectedGroup.group_type === group.group_type && selectedGroup.name === group.name
      ? null
      : { group_type: group.group_type, name: group.name };
    setSelectedGroup(newSelection);
  };

  const handleCreateGroup = async () => {
    setLoading(true);
    try {
      const payload = {
        group_type: formData.group_type,
        name: formData.name,
        description: formData.description,
      };
      const response = await axiosInstance.post("/create-group", payload);
      setDialog({
        isOpen: true,
        type: response.data.status,
        message: response.data.message || (response.data.status === "success" ? "Group created successfully!" : "Failed to create group"),
      });
      if (response.data.status === "success") {
        const groupsResponse = await axiosInstance.get("/group-lst");
        if (groupsResponse.data.status === "success") {
          setGroups(groupsResponse.data.message);
        }
        setFormData({
          group_type: "role",
          name: "",
          description: "",
        });
      }
    } catch (err) {
      if (err.response?.status !== 403) {
        setDialog({
          isOpen: true,
          type: "error",
          message: err.response?.data?.message || "Error creating group",
        });
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    setFormData({
      group_type: "role",
      name: "",
      description: "",
    });
  };

  const handleToggleFunctionality = (functionality) => {
    setExpandedFunctionalities((prev) => ({
      ...prev,
      [functionality]: !prev[functionality],
    }));
  };

  const handleAssignRight = (functionality, permission = null) => {
    if (permission) {
      if (!assignedRights.includes(permission)) {
        setAssignedRights((prev) => [...prev, permission]);
      }
    } else {
      const permissions = availableRights[functionality] || [];
      setAssignedRights((prev) => [
        ...prev,
        ...permissions.filter((p) => !prev.includes(p)),
      ]);
    }
  };

  const handleUnassignRight = (permission) => {
    setAssignedRights((prev) => prev.filter((item) => item !== permission));
  };

  const handleUpdateRights = async () => {
    if (!selectedGroup) {
      setDialog({
        isOpen: true,
        type: "error",
        message: "No group selected to assign rights.",
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        group_type: selectedGroup.group_type,
        value: selectedGroup.name,
        rights: assignedRights,
      };
      const response = await axiosInstance.post("/update-group-rights", payload);
      setGroups(
        groups.map((group) =>
          group.group_type === selectedGroup.group_type && group.name === selectedGroup.name
            ? { ...group, rights: assignedRights }
            : group
        )
      );
      setDialog({
        isOpen: true,
        type: response.data.status,
        message: response.data.message || "Group rights updated successfully!",
      });
    } catch (err) {
      if (err.response?.status !== 403) {
        setDialog({
          isOpen: true,
          type: "error",
          message: err.response?.data?.message || "Error updating group rights",
        });
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "create":
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">Create Group</h2>
            {loading && (
              <div className="flex justify-center mb-4">
                <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!formData.name) {
                  setDialog({
                    isOpen: true,
                    type: "error",
                    message: "Group name is required.",
                  });
                  return;
                }
                setDialog({
                  isOpen: true,
                  type: "prompt",
                  message: "Are you sure you want to create this group?",
                  onConfirm: handleCreateGroup,
                });
              }}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">Group Type</label>
                <select
                  name="group_type"
                  value={formData.group_type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="role">Role</option>
                  <option value="profile">Profile</option>
                  <option value="department">Department</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                    formData.name === "" && dialog.message ? "border-red-500" : ""
                  }`}
                  required
                />
                {formData.name === "" && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Name is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  rows="4"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={loading}
                >
                  Create Group
                </button>
                <button
                  type="button"
                  onClick={handleClearForm}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  <MdClear className="inline h-5 w-5 mr-2" />
                  Clear Form
                </button>
              </div>
            </form>
          </div>
        );
      case "list":
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">Group Listing</h2>
            {loading && (
              <div className="flex justify-center mb-4">
                <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
              </div>
            )}
            <DialogBox
              type={dialog.type}
              message={dialog.message}
              onConfirm={dialog.onConfirm}
              onCancel={dialog.onCancel}
              isOpen={dialog.isOpen}
              setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
            />
            {!loading && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Used In Count</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created On</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groups.map((group, index) => (
                      <tr key={`${group.group_type}-${group.name}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <input
                            type="checkbox"
                            checked={selectedGroup && selectedGroup.group_type === group.group_type && selectedGroup.name === group.name}
                            onChange={() => handleGroupSelect(group)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.group_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{group.description || "N/A"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.used_in_count || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.created_by || "N/A"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.created_on || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      case "rights":
        if (!selectedGroup) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800">Assign Rights to Group</h2>
              <DialogBox
                type="error"
                message="No group selected to assign rights."
                isOpen={true}
                setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
              />
            </div>
          );
        }
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">Assign Rights to Group</h2>
            {loading && (
              <div className="flex justify-center mb-4">
                <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
              </div>
            )}
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-700">Selected Group</h3>
              <p className="text-sm text-gray-700">
                {selectedGroup.group_type}: {selectedGroup.name}
              </p>
            </div>
            <div className="flex space-x-4">
              <div className="w-1/2 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Available Rights</h3>
                <div className="space-y-2">
                  {Object.keys(availableRights).map((functionality) => (
                    <div key={functionality} className="border-b border-gray-300">
                      <div
                        className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                        onClick={() => handleToggleFunctionality(functionality)}
                      >
                        <span>{functionality}</span>
                        <button
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignRight(functionality);
                          }}
                        >
                          Select All
                        </button>
                        {expandedFunctionalities[functionality] ? (
                          <MdExpandLess className="h-5 w-5" />
                        ) : (
                          <MdExpandMore className="h-5 w-5" />
                        )}
                      </div>
                      {expandedFunctionalities[functionality] && (
                        <div className="ml-4 space-y-1">
                          {availableRights[functionality].map((permission) => (
                            <div
                              key={permission}
                              className="p-2 bg-white rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                              onClick={() => handleAssignRight(functionality, permission)}
                            >
                              {permission}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-1/2 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Assigned Rights</h3>
                <div className="space-y-2">
                  {assignedRights.map((right) => (
                    <div
                      key={right}
                      className="p-2 bg-white rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                      onClick={() => handleUnassignRight(right)}
                    >
                      {right}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button
              className="w-full mt-4 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
              onClick={handleUpdateRights}
              disabled={loading}
            >
              Update Rights
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <div className="flex border-b border-gray-200 bg-white rounded-t-lg shadow-sm">
        {[
          { tab: "list", label: "List Groups", icon: MdList },
          { tab: "create", label: "Create Group", icon: MdPersonAdd },
          { tab: "rights", label: "Assign Rights", icon: MdLock },
        ].map(({ tab, label, icon: Icon }) => (
          <button
            key={tab}
            className={`flex-1 py-3 px-4 text-center text-sm font-medium transition-colors ${
              activeTab === tab
                ? "border-b-2 border-indigo-600 text-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => {
              if (tab === "rights" && !selectedGroup) {
                setDialog({
                  isOpen: true,
                  type: "error",
                  message: "No group selected to assign rights.",
                });
              } else if (tab === "rights" && selectedGroup) {
                fetchGroupPermissions(selectedGroup.group_type, selectedGroup.name);
                setActiveTab(tab);
              } else {
                setActiveTab(tab);
              }
            }}
          >
            <Icon className="inline h-5 w-5 mr-2" />
            {label}
          </button>
        ))}
      </div>
      <div className="mt-4">{renderTabContent()}</div>
      {dialog.isOpen && activeTab !== "list" && (
        <DialogBox
          type={dialog.type}
          message={dialog.message}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          isOpen={dialog.isOpen}
          setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
        />
      )}
    </div>
  );
};

export default GroupMaintenance;